import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Loader2, TrendingUp } from 'lucide-react';
import { API } from '../../../lib/api';
import { useInvoiceAccess } from '../../../lib/invoiceAccess';
import { field, ghost, rs, useApi } from '../purchasing/ui';
import { NoteActions, noteLabel } from '../../../components/admin/notes/NoteActions';

const FILTERS = [['party', 'Side', [['', 'All'], ['customer', 'Customer'], ['supplier', 'Supplier']]],
  ['kind', 'Type', [['', 'All'], ['credit', 'Credit'], ['debit', 'Debit']]],
  ['status', 'Status', [['', 'All'], ['issued', 'Issued'], ['cancelled', 'Cancelled']]]];

// All credit and debit notes, with filters and a CSV export.
export default function NotesList() {
  const { call } = useApi();
  const { canEdit } = useInvoiceAccess();
  const [f, setF] = useState({ party: '', kind: '', status: '', reason: '', q: '', date_from: '', date_to: '' });
  const [rows, setRows] = useState(null);
  const [reasons, setReasons] = useState([]);
  useEffect(() => {
    call('get', '/notes/reasons').then((d) => setReasons([...new Set([...d.customer.credit, ...d.customer.debit, ...d.supplier])])).catch(() => {});
  }, [call]);
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
  const load = useCallback(() => call('get', `/notes?${qs}`).then(setRows).catch(() => setRows([])), [call, qs]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="notes-list-page">
      <div className="flex flex-wrap items-end gap-3">
        <div><h1 className="text-xl font-semibold text-slate-900">Credit &amp; Debit Notes</h1>
          <p className="text-sm text-slate-500">Corrections to issued invoices and vendor bills. Raise them from the invoice or the bill.</p></div>
        <Link to="/admin/gst/notes/trends" className={`${ghost} ml-auto`} data-testid="notes-trends-link"><TrendingUp size={14} /> Trends</Link>
        <a href={`${API}/notes/export?${qs}`} className={ghost} data-testid="notes-export"><Download size={14} /> Export</a>
      </div>
      <div className="flex flex-wrap gap-2 items-end bg-white border border-slate-200 rounded-xl p-3">
        {FILTERS.map(([k, label, opts]) => (
          <label key={k} className="text-xs text-slate-500">{label}<select className={`${field} mt-1`} value={f[k]} onChange={set(k)} data-testid={`notes-filter-${k}`}>{opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></label>
        ))}
        <label className="text-xs text-slate-500">From<input type="date" className={`${field} mt-1`} value={f.date_from} onChange={set('date_from')} data-testid="notes-filter-from" /></label>
        <label className="text-xs text-slate-500">To<input type="date" className={`${field} mt-1`} value={f.date_to} onChange={set('date_to')} data-testid="notes-filter-to" /></label>
        <label className="text-xs text-slate-500 flex-1 min-w-[160px]">Customer, vendor or number<input className={`${field} mt-1`} value={f.q} onChange={set('q')} data-testid="notes-filter-q" /></label>
        <label className="text-xs text-slate-500">Reason<select className={`${field} mt-1`} value={f.reason} onChange={set('reason')} data-testid="notes-filter-reason">
          <option value="">All</option>{reasons.map((r) => <option key={r}>{r}</option>)}</select></label>
      </div>
      {!rows ? <Loader2 className="animate-spin text-slate-400" /> : rows.length === 0 ? <p className="text-sm text-slate-400" data-testid="notes-empty">No notes match.</p> : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm" data-testid="notes-table">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr>{['Number', 'Date', 'Type', 'Against', 'Party', 'Reason', 'Total', ''].map((h) => <th key={h} className="p-3">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">{rows.map((n) => (
              <tr key={n.id} className={n.status === 'cancelled' ? 'opacity-60' : ''} data-testid={`notes-row-${n.id}`}>
                <td className="p-3 font-mono">{n.number}{n.status === 'cancelled' && <span className="ml-1 text-[10px] uppercase font-semibold text-slate-500">Cancelled</span>}</td>
                <td className="p-3">{n.note_date}</td>
                <td className="p-3"><span className={`text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 ${n.kind === 'credit' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{noteLabel(n)}</span></td>
                <td className="p-3 font-mono text-xs">{n.invoice_number || `${n.bill_number} ${n.bill_invoice_no || ''}`}</td>
                <td className="p-3">{(n.customer_snapshot || {}).name || n.vendor_name}</td>
                <td className="p-3 text-slate-600">{n.reason}</td>
                <td className="p-3 font-mono text-right">{rs(n.totals.grand_total)}</td>
                <td className="p-3"><NoteActions n={n} canEdit={canEdit} onChanged={load} /></td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
